import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { watchImpressions, Options, Watcher } from './watchImpressions';

export type UseElementImpressionOptions = Options & {
  /**
   * When false, no watcher runs: nothing is attached, and a previously
   * attached watcher is stopped so a later intersection can't record against
   * a state that is no longer showing (e.g. a CTA whose tab was switched
   * away). Deduping repeat impressions across re-attachments is handled by
   * watchImpressions' own singleton (keyed by source:entity_type:entity_id).
   */
  active?: boolean;
};

/**
 * Attaches impression tracking to an existing rendered element via its ref —
 * unlike ImpressionWatcher this adds NO wrapper element and installs NO click
 * handler.
 *
 * Default to ImpressionWatcher. Reach for this hook when either constraint
 * applies to the element under measurement:
 *
 *  - It must not gain a wrapper. Wrapping the tabs hero's CTA bar in
 *    ImpressionWatcher's default div measurably displaced its price row and
 *    button (see PR #52899's layout experiment). `as` doesn't cover these call
 *    sites either: it forwards only className/style/onClick to the rendered
 *    component (everything else lands in watchImpressions' options, so
 *    `as={Button}` would swallow Button props), and a node carrying TWO sources
 *    — the tab-gated CTA bar — would need nested watchers, reintroducing the
 *    wrapper.
 *  - It must not own clicks. ImpressionWatcher's onClick calls watcher.click()
 *    on every press anywhere inside it, which emits a trackClick and rewrites
 *    the js_impression_nav_from cookie. On a CTA that is noise; on a page-level
 *    container it is wrong, because React bubbles innermost-first, so the
 *    outer handler runs last and clobbers the nav-from id that the specific
 *    inner watcher just set.
 *
 * The effect deliberately has NO dependency array: it re-checks the ref after
 * every commit, so an element that mounts in a later commit than the hook's
 * first run (a skeleton clearing, a fetch resolving) or is replaced by a
 * fresh node is picked up without the caller having to synchronize `active`
 * with mount timing. The re-check is a no-op (one ref comparison) while the
 * watched element is unchanged; options are read at attach time only.
 */
export function useElementImpression(
  ref: RefObject<HTMLElement | null>,
  options: UseElementImpressionOptions,
): void {
  const { active = true } = options;

  const watcherRef = useRef<Watcher | null>(null);
  const watchedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) {
      watcherRef.current?.stop();
      watcherRef.current = null;
      watchedElementRef.current = null;
      return;
    }
    const element = ref.current;
    if (element === watchedElementRef.current) {
      return;
    }
    // The element changed — including to null (unmounted while active): stop
    // the old watcher either way so no observer lingers on a detached node.
    watcherRef.current?.stop();
    watcherRef.current = null;
    watchedElementRef.current = element;
    if (!element) {
      return;
    }
    const { active: _active, ...watchOptions } = options;
    watcherRef.current = watchImpressions(element, watchOptions);
  });

  useEffect(
    () => () => {
      watcherRef.current?.stop();
    },
    [],
  );
}
